// frontend/src/pages/crm/fieldTargetPatch.ts
// Phase 5 (2026-07-29) — the null-vs-omitted rule for PUT /field-targets.
//
// 🔒 DELIBERATELY REACT-FREE, in its own module. Everything else in this phase
// is unverifiable locally because the frontend does not compile (react does not
// resolve), and this is the one piece where getting it wrong silently destroys
// data a manager never touched. Keeping it importable without react is what
// makes `fieldTargetPatch.test.ts` runnable at all.

export const TARGET_AXES = [
  'leadsCaptured',
  'meetingsHeld',
  'coloursAdvanced',
  'daysOut',
] as const;

export type TargetAxisKey = (typeof TARGET_AXES)[number];

/**
 * 🔒 THE NULL-VS-OMITTED RULE.
 *
 *   unchanged since load → OMIT the key    → the stored value is left alone
 *   changed to blank     → send `null`     → the axis becomes UNMEASURED
 *   changed to a number  → send the integer
 *
 * Sending every field on every save would wipe axes the manager never looked
 * at. Coercing blank to 0 would turn "not measured" into "target zero", which
 * the board then renders as 100% attained for doing nothing — the opposite of
 * the intended meaning.
 *
 * Junk (negative, non-numeric) is DROPPED rather than sent: the API rejects it
 * with a 400 that names a field the manager cannot see, so silently not
 * touching that axis is the less confusing failure.
 */
export function buildTargetPatch(
  initial: Partial<Record<TargetAxisKey, string>>,
  current: Partial<Record<TargetAxisKey, string>>,
): Partial<Record<TargetAxisKey, number | null>> {
  const patch: Partial<Record<TargetAxisKey, number | null>> = {};
  for (const key of TARGET_AXES) {
    const before = (initial[key] ?? '').trim();
    const after = (current[key] ?? '').trim();
    if (before === after) continue; // omitted → untouched
    if (after === '') {
      patch[key] = null; // explicit clear → unmeasured
      continue;
    }
    const n = Number(after);
    if (!Number.isFinite(n) || n < 0) continue; // API wants int >= 0
    patch[key] = Math.round(n);
  }
  return patch;
}

/** The stored target as an <input> string. Null (unmeasured) is BLANK, not 0. */
export function axisToInput(target: number | null | undefined): string {
  return target == null ? '' : String(target);
}
